/**
 * DIORAO cart rewards (own replacement for a paid gift-ladder app).
 *
 * - Keeps gift lines in sync with the cart: adds a gift when its threshold is
 *   reached, removes it when the cart drops below. Gift products are priced €0
 *   (compare-at = value). Not Buy X Get Y discounts: Shopify lets each cart line
 *   satisfy only one BXGY, so stacked gifts would need far more than the shown
 *   thresholds. If a gift line ever ends up not free it is removed again.
 * - Respects a manual removal of a gift for the rest of the session.
 * - <diorao-cart-rewards> renders the progress bar (shadow DOM, so the theme's
 *   section morphing cannot wipe it).
 * - <diorao-cart-upsell> renders "Complete the scene" suggestions in the drawer.
 *
 * Config comes from snippets/diorao-cart-rewards-config.liquid.
 */
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const cfgEl = document.getElementById('diorao-rewards-config');
const CFG = cfgEl ? JSON.parse(cfgEl.textContent) : null;
const SOURCE = 'diorao-rewards';
const I18N = CFG?.i18n || {};
const t = (key, fallback, vars = {}) => (I18N[key] || fallback).replace(/\{(\w+)\}/g, (_, k) => (k in vars ? vars[k] : ''));
const GIFT_PROP = '_diorao_gift';
const DISMISSED_KEY = 'diorao_gifts_dismissed';
const PREV_KEY = 'diorao_gifts_prev';

/* ---------- helpers ---------- */

const rate = () => parseFloat(window.Shopify?.currency?.rate || '1') || 1;
const toLocal = (shopCents) => Math.round(shopCents * rate());
const giftIds = () => new Set((CFG?.gifts || []).map((g) => g.variantId));

const isGiftLine = (item) =>
  giftIds().has(item.variant_id) || item.product_type === 'Gift' || (item.properties && item.properties[GIFT_PROP]);

/** Qualifying subtotal: everything except gift lines, after line discounts (presentment cents). */
function qualifying(cart) {
  let subtotal = 0;
  let count = 0;
  for (const item of cart.items) {
    if (isGiftLine(item)) continue;
    subtotal += item.final_line_price;
    count += item.quantity;
  }
  return { subtotal, count };
}

/** Builds a formatter from a rendered sample of 1234.56 in the current currency. */
const formatMoney = (() => {
  const sample = CFG?.moneySample || '€1,234.56';
  const m = sample.match(/1(\D?)234(?:(\D)(56))?/);
  const thousands = m ? m[1] : ',';
  const decimal = m && m[2] ? m[2] : '';
  const template = m ? sample.replace(m[0], '{n}') : '{n}';
  return (cents, short = false) => {
    const value = cents / 100;
    const whole = short && Math.round(value) === value;
    const fixed = decimal && !whole ? value.toFixed(2) : Math.round(value).toString();
    let [int, frac] = fixed.split('.');
    int = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return template.replace('{n}', frac ? int + decimal + frac : int);
  };
})();

function readJSON(key, fallback) {
  try {
    const v = sessionStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch (e) {
    return fallback;
  }
}
function writeJSON(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
}

async function fetchCart() {
  const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
  return res.json();
}
async function post(path, body) {
  const res = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}
const looksLikeCart = (r) => r && Array.isArray(r.items) && typeof r.item_count === 'number' && 'token' in r;

/** Milestones in presentment cents, sorted by threshold. */
function milestones() {
  const list = (CFG?.gifts || []).map((g) => ({
    kind: 'gift',
    label: g.label,
    title: g.title,
    variantId: g.variantId,
    threshold: Math.max(1, toLocal(g.threshold)),
  }));
  const country = window.Shopify?.country || CFG?.country;
  const currency = window.Shopify?.currency?.active || CFG?.currency;
  let ship;
  if ((CFG?.shipNaCountries || []).includes(country)) {
    ship = currency === 'USD' ? CFG.shipNaUsd : toLocal(CFG.shipNaEur);
  } else {
    ship = toLocal(CFG?.shipEu || 0);
  }
  if (ship > 0) list.push({ kind: 'shipping', label: t('shipping_label', 'Shipping'), title: t('free_shipping', 'free shipping'), threshold: ship });
  return list.sort((a, b) => a.threshold - b.threshold);
}

/* ---------- gift sync ---------- */

let busy = false;
let pending = false;
const failed = new Set();

async function reconcile(knownCart) {
  if (!CFG?.enabled || !CFG.gifts?.length) return;
  if (busy) {
    pending = true;
    return;
  }
  busy = true;
  try {
    let cart = looksLikeCart(knownCart) ? knownCart : await fetchCart();
    const { subtotal, count } = qualifying(cart);
    const dismissed = readJSON(DISMISSED_KEY, {});
    const prev = readJSON(PREV_KEY, []);
    const updates = {};
    const adds = [];

    for (const g of CFG.gifts) {
      const lines = cart.items.filter((i) => i.variant_id === g.variantId);
      const threshold = Math.max(1, toLocal(g.threshold));
      const qualifies = count > 0 && subtotal >= threshold;

      // Gift was in the cart after our last sync, the cart still qualifies, now it is gone: the shopper removed it.
      if (qualifies && !lines.length && prev.includes(g.variantId)) dismissed[g.variantId] = true;
      if (!qualifies) delete dismissed[g.variantId];

      const failKey = `${g.variantId}:${subtotal}`;
      if (qualifies && !dismissed[g.variantId] && !failed.has(failKey)) {
        if (!lines.length) adds.push({ id: g.variantId, quantity: 1, properties: { [GIFT_PROP]: '1' } });
        lines.forEach((l, idx) => {
          const want = idx === 0 ? 1 : 0;
          if (l.quantity !== want) updates[l.key] = want;
        });
      } else {
        lines.forEach((l) => (updates[l.key] = 0));
      }
    }

    let changed = false;
    if (Object.keys(updates).length) {
      await post('cart/update.js', { updates });
      changed = true;
    }
    if (adds.length) {
      await post('cart/add.js', { items: adds });
      changed = true;
    }
    if (changed) {
      cart = await fetchCart();
      // Safety net: a gift must come out free. If the discount did not apply, take it back out.
      const notFree = cart.items.filter((i) => giftIds().has(i.variant_id) && i.final_line_price > 0);
      if (notFree.length) {
        const sub = qualifying(cart).subtotal;
        const fix = {};
        notFree.forEach((i) => {
          fix[i.key] = 0;
          failed.add(`${i.variant_id}:${sub}`);
        });
        await post('cart/update.js', { updates: fix });
        cart = await fetchCart();
      }
      document.dispatchEvent(new CartUpdateEvent(cart, SOURCE, { itemCount: cart.item_count, source: SOURCE }));
    }

    writeJSON(DISMISSED_KEY, dismissed);
    writeJSON(
      PREV_KEY,
      cart.items.filter((i) => giftIds().has(i.variant_id)).map((i) => i.variant_id)
    );
    document.querySelectorAll('diorao-cart-rewards').forEach((el) => el.update(cart));
  } catch (e) {
    console.warn('[diorao-rewards]', e);
  } finally {
    busy = false;
    if (pending) {
      pending = false;
      reconcile();
    }
  }
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const detail = /** @type {any} */ (event).detail || {};
  if (detail.sourceId === SOURCE) return;
  if (detail.data?.didError) return;
  reconcile(detail.resource);
});

if (CFG?.enabled && CFG.itemCount > 0) reconcile();

/* Announcement bar, PDP: thresholds in the visitor's currency + delivery estimate */
if (CFG?.enabled) {
  const ship = milestones().find((m) => m.kind === 'shipping');
  if (ship) document.querySelectorAll('[data-diorao-ship-threshold]').forEach((el) => (el.textContent = formatMoney(ship.threshold, true)));
  document.querySelectorAll('[data-diorao-eur]').forEach((el) => {
    el.textContent = formatMoney(toLocal(parseInt(el.dataset.dioraoEur, 10) || 0), true);
  });
}

// Production 2-5 business days + transit per zone (see shipping policy / FAQ).
const TRANSIT = { pl: [1, 2], eu: [3, 6], na: [7, 12] };
function addBusinessDays(date, days) {
  const d = new Date(date);
  while (days > 0) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) days--;
  }
  return d;
}
document.querySelectorAll('[data-diorao-eta]').forEach((el) => {
  const [tMin, tMax] = TRANSIT[el.dataset.dioraoEta] || TRANSIT.eu;
  const fmt = new Intl.DateTimeFormat(CFG?.lang || document.documentElement.lang || 'en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const now = new Date();
  el.textContent = `${fmt.format(addBusinessDays(now, 2 + tMin))} – ${fmt.format(addBusinessDays(now, 5 + tMax))}`.toUpperCase();
});

/* ---------- add whole scene ---------- */

class DioraoAddScene extends HTMLElement {
  connectedCallback() {
    const btn = this.querySelector('button');
    if (!btn || btn.dataset.bound) return;
    btn.dataset.bound = '1';
    btn.addEventListener('click', async () => {
      let ids = [];
      try {
        ids = JSON.parse(this.dataset.variants || '[]');
      } catch (e) {}
      if (!ids.length) return;
      const label = btn.innerHTML;
      btn.disabled = true;
      btn.textContent = t('adding', 'ADDING…');
      try {
        await post('cart/add.js', { items: ids.map((id) => ({ id, quantity: 1 })) });
        const cart = await fetchCart();
        document.dispatchEvent(new CartUpdateEvent(cart, 'diorao-scene', { itemCount: cart.item_count, source: 'diorao-scene' }));
        document.querySelector('cart-drawer-component')?.open?.();
      } catch (e) {
        console.warn('[diorao-scene]', e);
      } finally {
        btn.disabled = false;
        btn.innerHTML = label;
      }
    });
  }
}
if (!customElements.get('diorao-add-scene')) customElements.define('diorao-add-scene', DioraoAddScene);

/* ---------- progress bar ---------- */

const ICON_GIFT =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 11h16v9H4zM3 7h18v4H3zM12 7v13M12 7c-1.5-3-5-3.5-5.5-1.5S9 7 12 7zm0 0c1.5-3 5-3.5 5.5-1.5S15 7 12 7z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>';
const ICON_SHIP =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 6h12v10H2zM14 9h4l3 3v4h-7M6 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm11 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>';

const BAR_CSS = `
  :host{display:block;font-family:var(--font-body,"Inter",Arial,sans-serif);color:var(--diorao-ink-900,#0B0B0D)}
  .wrap{padding:14px 16px 12px;background:var(--diorao-marble,#F2EDE3);border:1px solid rgba(176,114,54,.35);border-radius:4px}
  .msg{margin:0 0 14px;font-size:13px;line-height:1.4;text-align:center}
  .msg strong{color:var(--diorao-bronze-600,#8A5A2B)}
  .track{position:relative;height:4px;margin:0 14px 0 4px;background:rgba(11,11,13,.12);border-radius:2px}
  .fill{position:absolute;left:0;top:0;bottom:0;background:var(--diorao-bronze-500,#B07236);border-radius:2px;transition:width .4s cubic-bezier(.2,0,0,1)}
  .node{position:absolute;top:50%;transform:translate(-50%,-50%);width:26px;height:26px;border-radius:50%;display:grid;place-items:center;background:#fff;border:1.5px solid rgba(11,11,13,.25);color:rgba(11,11,13,.45);transition:background .3s,border-color .3s,color .3s}
  .node svg{width:14px;height:14px}
  .node.on{background:var(--diorao-bronze-500,#B07236);border-color:var(--diorao-bronze-500,#B07236);color:#fff}
.labels{position:relative;height:36px;margin:14px 14px 0 4px}
  .label{position:absolute;transform:translateX(-50%);width:58px;text-align:center;font-family:var(--font-mono,"JetBrains Mono",monospace);font-size:8.5px;line-height:1.3;letter-spacing:.04em;text-transform:uppercase;color:rgba(11,11,13,.55)}
  .label.on{color:var(--diorao-bronze-600,#8A5A2B);font-weight:600}
  .label:last-child{transform:translateX(calc(-100% + 13px));text-align:right}
  @media (prefers-reduced-motion:reduce){.fill,.node{transition:none}}
`;

class DioraoCartRewards extends HTMLElement {
  static observedAttributes = ['data-subtotal', 'data-count'];

  connectedCallback() {
    if (!this.shadowRoot) this.attachShadow({ mode: 'open' });
    this.render();
  }

  attributeChangedCallback() {
    if (this.shadowRoot) this.render();
  }

  /** @param {any} cart */
  update(cart) {
    const q = qualifying(cart);
    this.dataset.subtotal = String(q.subtotal);
    this.dataset.count = String(q.count);
  }

  render() {
    if (!CFG?.enabled) return;
    const subtotal = parseInt(this.dataset.subtotal || '0', 10);
    const count = parseInt(this.dataset.count || '0', 10);
    const ms = milestones();
    if (!ms.length) return;
    const reached = (m) => count > 0 && subtotal >= m.threshold;
    const next = ms.find((m) => !reached(m));

    let msg;
    if (!next) {
      msg = t('all_unlocked', 'All rewards unlocked. <strong>Free shipping and every gift</strong> are yours.');
    } else {
      const left = formatMoney(next.threshold - (count > 0 ? subtotal : 0));
      msg =
        next.kind === 'shipping'
          ? t('add_for_shipping', 'Add <strong>{amount}</strong> for <strong>free shipping</strong>', { amount: left })
          : count === 0
          ? t('add_any_for_gift', 'Add any figure to get a <strong>free {gift}</strong>', { gift: next.title })
          : t('add_for_gift', 'Add <strong>{amount}</strong> for a <strong>free {gift}</strong>', { amount: left, gift: next.title });
    }

    // Evenly spaced nodes; the fill interpolates inside the current segment.
    const n = ms.length;
    const pos = (i) => ((i + 1) / n) * 100;
    let fill = 0;
    for (let i = 0; i < n; i++) {
      const from = i === 0 ? 0 : ms[i - 1].threshold;
      const to = ms[i].threshold;
      if (count > 0 && subtotal >= to) {
        fill = pos(i);
        continue;
      }
      const start = i === 0 ? 0 : pos(i - 1);
      const ratio = count > 0 ? Math.max(0, Math.min(1, (subtotal - from) / Math.max(1, to - from))) : 0;
      fill = start + (pos(i) - start) * ratio;
      break;
    }

    this.shadowRoot.innerHTML = `
      <style>${BAR_CSS}</style>
      <div class="wrap" role="group" aria-label="${t('aria', 'Order rewards')}">
        <p class="msg" aria-live="polite">${msg}</p>
        <div class="track">
          <div class="fill" style="width:${fill.toFixed(1)}%"></div>
          ${ms
            .map(
              (m, i) =>
                `<span class="node${reached(m) ? ' on' : ''}" style="left:${pos(i)}%" title="${m.label}">${
                  m.kind === 'shipping' ? ICON_SHIP : ICON_GIFT
                }</span>`
            )
            .join('')}
        </div>
        <div class="labels">
          ${ms
            .map(
              (m, i) =>
                `<span class="label${reached(m) ? ' on' : ''}" style="left:${pos(i)}%">${m.label}<br>${
                  m.threshold <= 1 ? t('any_order', 'Any order') : formatMoney(m.threshold, true)
                }</span>`
            )
            .join('')}
        </div>
      </div>`;
  }
}
if (!customElements.get('diorao-cart-rewards')) customElements.define('diorao-cart-rewards', DioraoCartRewards);

/* ---------- complete the scene ---------- */

const UPSELL_CSS = `
  :host{display:block;font-family:var(--font-body,"Inter",Arial,sans-serif);color:var(--diorao-ink-900,#0B0B0D)}
  h3{margin:0 0 10px;font-family:var(--font-mono,"JetBrains Mono",monospace);font-size:11px;font-weight:500;letter-spacing:.16em;text-transform:uppercase;color:var(--diorao-bronze-600,#8A5A2B)}
  .list{display:grid;gap:10px}
  .card{display:grid;grid-template-columns:56px 1fr auto;gap:12px;align-items:center;padding:8px;border:1px solid rgba(11,11,13,.12);border-radius:4px;background:#fff}
  .card img{width:56px;height:56px;object-fit:cover;border-radius:2px;background:#eee}
  .t{margin:0;font-size:13px;font-weight:600;line-height:1.3}
  .t a{color:inherit;text-decoration:none}
  .p{margin:2px 0 0;font-size:12px;color:rgba(11,11,13,.6)}
  button{height:34px;padding:0 14px;border:0;border-radius:4px;background:var(--diorao-ink-900,#0B0B0D);color:var(--diorao-marble,#F2EDE3);font:700 11px/1 var(--font-body,"Inter",sans-serif);letter-spacing:.08em;text-transform:uppercase;cursor:pointer}
  button:hover{background:var(--diorao-bronze-600,#8A5A2B)}
  button[disabled]{opacity:.5;cursor:default}
`;

const upsellCache = new Map();

class DioraoCartUpsell extends HTMLElement {
  static observedAttributes = ['data-product-id', 'data-exclude', 'data-scale'];

  connectedCallback() {
    if (!this.shadowRoot) this.attachShadow({ mode: 'open' });
    this.load();
  }

  attributeChangedCallback() {
    if (this.shadowRoot) this.load();
  }

  async load() {
    const productId = this.dataset.productId;
    if (!productId) return;
    const exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean).map(Number));
    const scale = this.dataset.scale || '';
    try {
      let products = upsellCache.get(productId);
      if (!products) {
        const root = window.Shopify?.routes?.root || '/';
        const res = await fetch(`${root}recommendations/products.json?product_id=${productId}&limit=8&intent=related`);
        products = (await res.json()).products || [];
        upsellCache.set(productId, products);
      }
      const picks = products
        .filter((p) => !exclude.has(p.id) && p.type !== 'Gift' && p.available)
        .slice(0, 2)
        .map((p) => {
          // scale option = values like 1:35 (names are translated per locale)
          const scaleIdx = (p.options || []).findIndex((o) => (o.values || []).some((v) => /^1:\d+$/.test(v)) || /scale|skala/i.test(o.name || o));
          const variant =
            (scaleIdx >= 0 && p.variants.find((v) => v.available && v.options[scaleIdx] === scale)) ||
            p.variants.find((v) => v.available);
          return { p, variant };
        })
        .filter((x) => x.variant);
      this.render(picks, scale);
    } catch (e) {
      this.shadowRoot.innerHTML = '';
    }
  }

  render(picks, scale) {
    if (!picks.length) {
      this.shadowRoot.innerHTML = '';
      return;
    }
    const img = (p) => (p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=120` : '');
    this.shadowRoot.innerHTML = `
      <style>${UPSELL_CSS}</style>
      <h3>${t('complete_scene', 'Complete the scene')}</h3>
      <div class="list">
        ${picks
          .map(
            ({ p, variant }) => `
          <div class="card">
            <img src="${img(p)}" alt="" loading="lazy" width="56" height="56">
            <div>
              <p class="t"><a href="${p.url}">${p.title}</a></p>
              <p class="p">${formatMoney(variant.price)}${scale && variant.options.includes(scale) ? ` · ${scale}` : ''}</p>
            </div>
            <button type="button" data-variant="${variant.id}">${t('add', 'Add')}</button>
          </div>`
          )
          .join('')}
      </div>`;
    this.shadowRoot.querySelectorAll('button[data-variant]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        btn.textContent = '…';
        try {
          await post('cart/add.js', { items: [{ id: Number(btn.dataset.variant), quantity: 1 }] });
          const cart = await fetchCart();
          document.dispatchEvent(new CartUpdateEvent(cart, 'diorao-upsell', { itemCount: cart.item_count, source: 'diorao-upsell' }));
        } catch (e) {
          btn.disabled = false;
          btn.textContent = t('add', 'Add');
        }
      });
    });
  }
}
if (!customElements.get('diorao-cart-upsell')) customElements.define('diorao-cart-upsell', DioraoCartUpsell);
